<template>
  <div class="overview-container">
    <div class="overview-header">
      <div class="header-content">
        <button @click.stop="goBack" class="back-button">
          <span class="back-icon">←</span>
          <span>返回</span>
        </button>
        <div class="header-title">
          <div class="title-icon">📊</div>
          <h1>数据概览</h1>
        </div>
      </div>
    </div>

    <div class="overview-content">
      <div class="overview-toolbar">
        <div class="toolbar-left">
          <span class="toolbar-title">自定义指标</span>
          <span class="toolbar-count">共 {{ sortedJobList.length }} 条</span>
        </div>
        <div class="toolbar-right">
          <button class="refresh-button summary-entry-button" type="button" @click="openMaterialScanSummary">
            素材汇总
          </button>
          <button class="refresh-button" type="button" :disabled="deleteLogsLoading" @click="openDeleteLogsDialog">
            {{ deleteLogsLoading ? '加载中...' : '查看处理记录' }}
          </button>
          <button class="action-button start" type="button" :disabled="batchActionLoading || loading" @click="handleBatchBoost(true)">
            一键起量
          </button>
          <button class="action-button stop" type="button" :disabled="batchActionLoading || loading" @click="handleBatchBoost(false)">
            一键停止
          </button>
          <label class="toolbar-label">
            投放状态
            <select v-model="selectedStatus" class="toolbar-select">
              <option value="">全部状态</option>
              <option v-for="option in statusFilterOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="toolbar-label">
            排序字段
            <select v-model="sortField" class="toolbar-select">
              <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="toolbar-label">
            顺序
            <select v-model="sortOrder" class="toolbar-select">
              <option value="desc">从高到低</option>
              <option value="asc">从低到高</option>
            </select>
          </label>
          <button class="refresh-button" type="button" :disabled="loading" @click="fetchJobList">
            {{ loading ? '加载中...' : '刷新' }}
          </button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-item">
          <div class="summary-label">汇总整体消耗</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.totalStatCost) }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">汇总净成交订单数</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.totalOrderCount, 0) }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">汇总净成交金额</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.totalOrderAmount) }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">汇总净成交ROI</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.avgSettleRoi) }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">汇总1小时内退款率</div>
          <div class="summary-value">{{ formatNumber(summaryMetrics.avgRefundRate) }}%</div>
        </div>
      </div>

      <div v-if="errorMessage" class="status-message error">{{ errorMessage }}</div>
      <div v-if="actionMessage" class="status-message action">{{ actionMessage }}</div>
      <div v-if="!loading && sortedJobList.length === 0" class="status-message empty">暂无数据</div>

      <div class="job-card-list">
        <div v-for="item in sortedJobList" :key="item.id || item.jobId" class="job-card">
          <div class="job-card-header">
            <div class="job-avatar">🏳️</div>
            <div class="job-base">
              <div class="target-name">{{ item.targetName || '-' }}</div>
              <div class="target-id">ID：{{ item.targetId || '-' }}</div>
              <div class="job-meta">任务：{{ item.jobName || '-' }} ｜ 投放状态：{{ getStatusText(item.status) }}</div>
              <div class="auto-material-row">
                <span class="auto-material-label">
                  自动处理素材：{{ item.autoMaterialEnabled ? '已开启' : '已关闭' }}
                </span>
                <button
                  class="auto-material-button enable"
                  type="button"
                  :disabled="isAutoMaterialSwitchSaving(item) || item.autoMaterialEnabled"
                  @click="toggleAutoMaterial(item, true)"
                >
                  {{ isAutoMaterialSwitchSaving(item) && autoMaterialPendingEnabled(item) ? '开启中...' : '打开自动处理素材' }}
                </button>
                <button
                  class="auto-material-button disable"
                  type="button"
                  :disabled="isAutoMaterialSwitchSaving(item) || !item.autoMaterialEnabled"
                  @click="toggleAutoMaterial(item, false)"
                >
                  {{ isAutoMaterialSwitchSaving(item) && !autoMaterialPendingEnabled(item) ? '关闭中...' : '关闭自动处理素材' }}
                </button>
              </div>
            </div>
          </div>

          <div class="metrics-grid primary-grid">
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.statCost) }}</div>
              <div class="metric-label">整体消耗</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.totalPayOrderGmvForRoi2) }}</div>
              <div class="metric-label">用户实际支付金额</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.totalPrepayAndPaySettleRoi21h) }}</div>
              <div class="metric-label">净成交ROI</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.totalOrderSettleAmountForRoi21h) }}</div>
              <div class="metric-label">净成交金额</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.totalOrderSettleCountForRoi21h, 0) }}</div>
              <div class="metric-label">净成交订单数</div>
            </div>
            <div class="metric-item">
              <div class="metric-value">{{ formatNumber(item.targetBlance) }}</div>
              <div class="metric-label">账户余额</div>
            </div>
          </div>

          <div class="metrics-grid secondary-grid">
            <div class="metric-item secondary">
              <div class="metric-value secondary-value">{{ formatNumber(item.totalCostPerPayOrderSettleForRoi21h) }}</div>
              <div class="metric-label">净成交订单成本</div>
            </div>
            <div class="metric-item secondary">
              <div class="metric-value secondary-value">{{ formatPercent(item.totalRefundOrderGmvForRoi21hRate) }}</div>
              <div class="metric-label">1小时内退款率</div>
            </div>
            <div class="metric-item secondary">
              <div class="metric-value secondary-value">{{ formatNumber(item.totalRefundOrderCountForRoi21h, 0) }}</div>
              <div class="metric-label">1小时内退款订单数</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="deleteLogsVisible" class="delete-log-modal" @click.self="closeDeleteLogsDialog">
      <div class="delete-log-dialog">
        <div class="delete-log-header">
          <div>
            <div class="delete-log-title">当天删除记录</div>
            <div class="delete-log-subtitle">展示今日已实际删除的素材及删除原因</div>
          </div>
          <button class="delete-log-close" type="button" @click="closeDeleteLogsDialog">关闭</button>
        </div>
        <div v-if="deleteLogsLoading" class="delete-log-status">删除记录加载中...</div>
        <div v-else-if="todayDeleteLogs.length === 0" class="delete-log-status">今日暂无删除记录</div>
        <div v-else class="delete-log-list">
          <div v-for="item in todayDeleteLogs" :key="`${item.jobId || ''}_${item.materialId || ''}_${item.deleteTime || ''}`" class="delete-log-card">
            <div class="delete-log-row">
              <span class="delete-log-key">删除时间</span>
              <span class="delete-log-value">{{ item.deleteTime || '-' }}</span>
            </div>
            <div class="delete-log-row">
              <span class="delete-log-key">店铺</span>
              <span class="delete-log-value">{{ item.targetName || '-' }} / {{ item.targetId || '-' }}</span>
            </div>
            <div class="delete-log-row">
              <span class="delete-log-key">计划</span>
              <span class="delete-log-value">{{ item.jobName || '-' }} / {{ item.jobId || '-' }}</span>
            </div>
            <div class="delete-log-row">
              <span class="delete-log-key">素材ID</span>
              <span class="delete-log-value">{{ item.materialId || '-' }}</span>
            </div>
            <div class="delete-log-row">
              <span class="delete-log-key">整体消耗</span>
              <span class="delete-log-value">{{ formatNumber(item.statCostForRoi2) }}</span>
            </div>
            <div class="delete-log-row">
              <span class="delete-log-key">净成交ROI</span>
              <span class="delete-log-value">{{ formatNumber(item.roi) }}</span>
            </div>
            <div class="delete-log-row">
              <span class="delete-log-key">所属档位</span>
              <span class="delete-log-value">{{ item.stage || '-' }}</span>
            </div>
            <div class="delete-log-reason">{{ item.reason || '-' }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'
import { createAdaptiveViewportMixin } from '@/mixins/adaptiveViewport'

const STATUS_MAP = {
  ADVERTISER_OFFLINE_BUDGET: '账户超出预算',
  ADVERTISER_PRE_OFFLINE_BUDGET: '账户接近预算',
  AUDIT: '新建审核中',
  AUDIT_STATUS_ERROR: '异常，请联系审核人员',
  AWEME_ACCOUNT_DISABLED: '关联抖音号不可投',
  CAMPAIGN_DISABLE: '已被计划组暂停',
  CAMPAIGN_OFFLINE_BUDGET: '计划组超出预算',
  CAMPAIGN_PRE_OFFLINE_BUDGET: '计划组接近预算',
  CREATE: '计划新建',
  DELETE: '已删除',
  DELIVERY_OK: '投放中',
  DISABLE: '已暂停',
  DRAFT: '草稿',
  ERROR: '数据错误',
  EXTERNAL_URL_DISABLE: '落地页暂不可用',
  FROZEN: '已终止',
  LIVE_ROOM_OFF: '关联直播间未开播',
  NO_SCHEDULE: '不在投放时段',
  OFFLINE_AUDIT: '审核不通过',
  OFFLINE_BALANCE: '账户余额不足',
  OFFLINE_BUDGET: '营销超出预算',
  PRE_OFFLINE_BUDGET: '营销接近预算',
  PRE_ONLINE: '预上线',
  QUOTA_DISABLE: 'QUOTA配额不足暂停计划',
  REAUDIT: '修改审核中',
  SYSTEM_DISABLE: '系统暂停',
  TIME_DONE: '已完成',
  TIME_NO_REACH: '未到达投放时间'
}

export default {
  name: 'DataOverview',
  mixins: [createAdaptiveViewportMixin({ designWidth: 1600, designHeight: 980, minScale: 0.5 })],
  data() {
    return {
      jobList: [],
      loading: false,
      listRequesting: false,
      autoRefreshTimer: null,
      cardNumber: '',
      errorMessage: '',
      selectedStatus: 'DELIVERY_OK',
      actionMessage: '',
      batchActionLoading: false,
      exportingPatrolLogs: false,
      deleteLogsVisible: false,
      deleteLogsLoading: false,
      todayDeleteLogs: [],
      autoMaterialSwitchSavingMap: {},
      autoMaterialSwitchPendingMap: {},
      boostBudget: '100',
      boostDuration: '24',
      sortField: 'statCost',
      sortOrder: 'desc',
      sortOptions: [
        { label: '整体消耗', value: 'statCost', type: 'number' },
        { label: '用户实际支付金额', value: 'totalPayOrderGmvForRoi2', type: 'number' },
        { label: '净成交ROI', value: 'totalPrepayAndPaySettleRoi21h', type: 'number' },
        { label: '净成交金额', value: 'totalOrderSettleAmountForRoi21h', type: 'number' },
        { label: '净成交订单数', value: 'totalOrderSettleCountForRoi21h', type: 'number' },
        { label: '账户余额', value: 'targetBlance', type: 'number' },
        { label: '1小时内退款率', value: 'totalRefundOrderGmvForRoi21hRate', type: 'number' },
        { label: '锚定账户名称', value: 'targetName', type: 'string' }
      ]
    }
  },
  computed: {
    statusFilterOptions() {
      return Object.keys(STATUS_MAP).map(code => ({
        value: code,
        label: STATUS_MAP[code]
      }))
    },
    filteredJobList() {
      if (!this.selectedStatus) {
        return this.jobList
      }
      return this.jobList.filter(item => item.status === this.selectedStatus)
    },
    sortedJobList() {
      const list = [...this.filteredJobList]
      const selectedOption = this.sortOptions.find(item => item.value === this.sortField)
      if (!selectedOption) {
        return list
      }
      const direction = this.sortOrder === 'asc' ? 1 : -1
      return list.sort((a, b) => {
        const aValue = a[this.sortField]
        const bValue = b[this.sortField]
        if (selectedOption.type === 'string') {
          const aString = String(aValue || '')
          const bString = String(bValue || '')
          return aString.localeCompare(bString) * direction
        }
        const aNumber = this.toNumber(aValue)
        const bNumber = this.toNumber(bValue)
        return (aNumber - bNumber) * direction
      })
    },
    summaryMetrics() {
      const totalStatCost = this.sortedJobList.reduce((sum, item) => sum + this.toNumber(item.statCost), 0)
      const totalOrderCount = this.sortedJobList.reduce((sum, item) => sum + this.toNumber(item.totalOrderSettleCountForRoi21h), 0)
      const totalOrderAmount = this.sortedJobList.reduce((sum, item) => sum + this.toNumber(item.totalOrderSettleAmountForRoi21h), 0)
      const totalRefundOrderCount = this.sortedJobList.reduce((sum, item) => sum + this.toNumber(item.totalRefundOrderCountForRoi21h), 0)
      const avgSettleRoi = totalStatCost > 0 ? totalOrderAmount / totalStatCost : 0
      const totalOrderBase = totalRefundOrderCount + totalOrderCount
      const avgRefundRate = totalOrderBase > 0 ? (totalRefundOrderCount / totalOrderBase) * 100 : 0
      return {
        totalStatCost,
        totalOrderCount,
        totalOrderAmount,
        avgSettleRoi,
        avgRefundRate
      }
    }
  },
  created() {
    this.cardNumber = this.$route.query.cardNumber || localStorage.getItem('cardNumber') || ''
    this.fetchJobList()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  methods: {
    applyAdaptiveViewportScale() {
      const el = this.$el
      if (!el) {
        return
      }
      const pageBackground = window.getComputedStyle(el).background || ''

      const isMobile = window.innerWidth <= 768
      if (isMobile) {
        document.documentElement.style.overflow = 'hidden'
        document.documentElement.style.width = '100%'
        document.documentElement.style.height = '100%'
        document.documentElement.style.background = pageBackground
        document.body.style.overflow = 'hidden'
        document.body.style.width = '100%'
        document.body.style.height = '100%'
        document.body.style.background = pageBackground

        el.style.zoom = '1'
        el.style.transformOrigin = 'top left'
        el.style.width = '100%'
        el.style.height = '100vh'
        el.style.minHeight = '100vh'
        el.style.overflowX = 'hidden'
        el.style.overflowY = 'auto'
        el.style.marginLeft = '0'
        el.style.marginRight = '0'
        return
      }

      const designWidth = 1600
      const designHeight = 980
      const minScale = 0.5
      const viewportWidth = Math.max(window.innerWidth || designWidth, 320)
      const viewportHeight = Math.max(window.innerHeight || designHeight, 560)
      const previousZoom = el.style.zoom
      const previousTransformOrigin = el.style.transformOrigin
      const previousWidth = el.style.width
      const previousHeight = el.style.height
      const previousMinHeight = el.style.minHeight
      const previousOverflow = el.style.overflow
      const previousMarginLeft = el.style.marginLeft
      const previousMarginRight = el.style.marginRight

      el.style.zoom = '1'
      el.style.transformOrigin = 'top left'
      el.style.width = `${designWidth}px`
      el.style.height = 'auto'
      el.style.minHeight = ''
      el.style.overflow = 'visible'
      el.style.marginLeft = '0'
      el.style.marginRight = '0'

      const contentHeight = Math.max(
        designHeight,
        el.scrollHeight || 0,
        el.offsetHeight || 0
      )

      el.style.zoom = previousZoom
      el.style.transformOrigin = previousTransformOrigin
      el.style.width = previousWidth
      el.style.height = previousHeight
      el.style.minHeight = previousMinHeight
      el.style.overflow = previousOverflow
      el.style.marginLeft = previousMarginLeft
      el.style.marginRight = previousMarginRight

      const widthScale = viewportWidth / designWidth
      const heightScale = viewportHeight / contentHeight
      const scale = Math.max(minScale, Math.min(widthScale, heightScale))
      const scaledWidth = designWidth * scale
      const horizontalMargin = Math.max((viewportWidth - scaledWidth) / 2 / scale, 0)

      document.documentElement.style.overflowX = 'hidden'
      document.documentElement.style.overflowY = 'auto'
      document.documentElement.style.width = '100%'
      document.documentElement.style.height = 'auto'
      document.documentElement.style.background = pageBackground
      document.body.style.overflowX = 'hidden'
      document.body.style.overflowY = 'hidden'
      document.body.style.width = '100%'
      document.body.style.height = 'auto'
      document.body.style.background = pageBackground

      el.style.zoom = String(scale)
      el.style.transformOrigin = 'top left'
      el.style.width = `${designWidth}px`
      el.style.height = 'auto'
      el.style.minHeight = `${contentHeight}px`
      el.style.overflowX = 'hidden'
      el.style.overflowY = 'visible'
      el.style.marginLeft = `${horizontalMargin}px`
      el.style.marginRight = `${horizontalMargin}px`
    },
    resetAdaptiveViewportScale() {
      const el = this.$el
      if (!el) {
        return
      }

      el.style.zoom = ''
      el.style.transformOrigin = ''
      el.style.width = ''
      el.style.height = ''
      el.style.minHeight = ''
      el.style.overflow = ''
      el.style.overflowX = ''
      el.style.overflowY = ''
      el.style.marginLeft = ''
      el.style.marginRight = ''

      const cache = this.__adaptiveViewportDocumentStyleCache || {}
      document.documentElement.style.overflow = cache.documentOverflow || ''
      document.documentElement.style.overflowX = ''
      document.documentElement.style.overflowY = ''
      document.documentElement.style.width = cache.documentWidth || ''
      document.documentElement.style.height = cache.documentHeight || ''
      document.documentElement.style.background = cache.documentBackground || ''
      document.body.style.overflow = cache.bodyOverflow || ''
      document.body.style.overflowX = ''
      document.body.style.overflowY = ''
      document.body.style.width = cache.bodyWidth || ''
      document.body.style.height = cache.bodyHeight || ''
      document.body.style.background = cache.bodyBackground || ''
    },
    toSafeHeaderValue(value) {
      const str = String(value ?? '')
      for (let i = 0; i < str.length; i += 1) {
        if (str.charCodeAt(i) > 255) {
          return encodeURIComponent(str)
        }
      }
      return str
    },
    goBack() {
      this.$router.push('/home')
    },
    openMaterialScanSummary() {
      this.$router.push('/material-scan-summary')
    },
    toNumber(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : 0
    },
    formatNumber(value, fractionDigits = 2) {
      const numberValue = this.toNumber(value)
      return numberValue.toLocaleString('zh-CN', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      })
    },
    formatPercent(value) {
      const numberValue = this.toNumber(value)
      if (numberValue <= 1) {
        return `${(numberValue * 100).toFixed(2)}%`
      }
      return `${numberValue.toFixed(2)}%`
    },
    getStatusText(status) {
      return STATUS_MAP[status] || status || '-'
    },
    normalizeJobList(list) {
      return (list || []).map(item => ({
        id: item.id,
        username: item.username ?? item.userName ?? item.user_name ?? '',
        advertiserId: item.advertiserId ?? item.advertiser_id ?? item.targetId ?? item.target_id,
        targetId: item.targetId ?? item.target_id,
        targetBlance: item.targetBlance ?? item.target_blance ?? item.targetBalance ?? item.target_balance,
        targetName: item.targetName ?? item.target_name,
        jobId: item.jobId ?? item.job_id,
        jobName: item.jobName ?? item.job_name,
        roi2Goal: item.roi2Goal ?? item.roi2_goal,
        status: item.status,
        statCost: item.statCost ?? item.stat_cost,
        totalPrepayAndPayOrderRoi2: item.totalPrepayAndPayOrderRoi2 ?? item.total_prepay_and_pay_order_roi2,
        totalPayOrderGmvForRoi2: item.totalPayOrderGmvForRoi2 ?? item.total_pay_order_gmv_for_roi2,
        totalCostPerPayOrderSettleForRoi21h: item.totalCostPerPayOrderSettleForRoi21h ?? item.total_cost_per_pay_order_settle_for_roi21h,
        totalOrderSettleCountForRoi21h: item.totalOrderSettleCountForRoi21h ?? item.total_order_settle_count_for_roi21h,
        totalOrderSettleAmountForRoi21h: item.totalOrderSettleAmountForRoi21h ?? item.total_order_settle_amount_for_roi21h,
        totalPrepayAndPaySettleRoi21h: item.totalPrepayAndPaySettleRoi21h ?? item.total_prepay_and_pay_settle_roi21h,
        totalRefundOrderGmvForRoi21hRate: item.totalRefundOrderGmvForRoi21hRate ?? item.total_refund_order_gmv_for_roi21h_rate,
        totalRefundOrderCountForRoi21h: item.totalRefundOrderCountForRoi21h ?? item.total_refund_order_count_for_roi21h,
        autoMaterialEnabled: !!(item.autoMaterialEnabled ?? item.auto_material_enabled ?? item.autoMaterialSwitchEnabled ?? item.auto_material_switch_enabled)
      }))
    },
    getJobAdId(item) {
      return item?.jobId ?? item?.job_id ?? ''
    },
    getJobAdvertiserId(item) {
      return item?.advertiserId ?? item?.targetId ?? ''
    },
    getJobUsername(item) {
      return String(item?.username ?? item?.userName ?? item?.user_name ?? '').trim()
    },
    getItemActionKey(item) {
      return `${this.getJobAdvertiserId(item)}_${this.getJobAdId(item)}`
    },
    getAutoMaterialSwitchKey(item) {
      return String(this.getJobAdId(item) || item?.id || '')
    },
    isAutoMaterialSwitchSaving(item) {
      const key = this.getAutoMaterialSwitchKey(item)
      return !!this.autoMaterialSwitchSavingMap[key]
    },
    autoMaterialPendingEnabled(item) {
      const key = this.getAutoMaterialSwitchKey(item)
      return !!this.autoMaterialSwitchPendingMap[key]
    },
    async toggleAutoMaterial(item, enabled) {
      const key = this.getAutoMaterialSwitchKey(item)
      const jobId = this.getJobAdId(item)
      const requestUsername = this.getJobUsername(item)
      if (!key || !jobId) {
        this.actionMessage = '缺少 jobId，无法设置自动处理素材'
        return
      }
      this.$set(this.autoMaterialSwitchSavingMap, key, true)
      this.$set(this.autoMaterialSwitchPendingMap, key, !!enabled)
      try {
        const token = localStorage.getItem('authToken')
        const safeToken = this.toSafeHeaderValue(token)
        const username = this.toSafeHeaderValue(requestUsername || '')
        const headers = {
          'Content-Type': 'application/json'
        }
        if (token) {
          headers.Authorization = `Bearer ${safeToken}`
        }
        if (username) {
          headers.username = username
        }
        const body = {
          jobId: Number(jobId),
          enabled: !!enabled
        }
        const response = await axios.post(API_CONFIG.jobAutoMaterialSwitch(), body, { headers })
        const data = response?.data || {}
        if (Number(data.code) === 200) {
          item.autoMaterialEnabled = !!enabled
          this.actionMessage = `${item.targetName || jobId} 已${enabled ? '打开' : '关闭'}自动处理素材`
        } else {
          this.actionMessage = data.msg || data.message || '自动处理素材开关设置失败'
        }
      } catch (error) {
        this.actionMessage = error.response?.data?.msg || error.response?.data?.message || '自动处理素材开关设置失败'
      } finally {
        this.$set(this.autoMaterialSwitchSavingMap, key, false)
        this.$delete(this.autoMaterialSwitchPendingMap, key)
      }
    },
    getDownloadFileName(response, fallbackName) {
      const directName = response?.headers?.['download-filename']
      if (directName) {
        try {
          return decodeURIComponent(directName)
        } catch (error) {
          return directName
        }
      }
      const disposition = response?.headers?.['content-disposition'] || ''
      const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)
      const encodedName = match?.[1] || match?.[2]
      if (!encodedName) {
        return fallbackName
      }
      try {
        return decodeURIComponent(encodedName)
      } catch (error) {
        return encodedName
      }
    },
    triggerBlobDownload(blob, fileName) {
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    },
    openDeleteLogsDialog() {
      this.deleteLogsVisible = true
      this.fetchTodayDeleteLogs()
    },
    closeDeleteLogsDialog() {
      this.deleteLogsVisible = false
    },
    async fetchTodayDeleteLogs() {
      if (this.deleteLogsLoading) {
        return
      }
      this.deleteLogsLoading = true
      try {
        const token = localStorage.getItem('authToken')
        const safeToken = this.toSafeHeaderValue(token)
        const response = await axios.get(API_CONFIG.jobMaterialDeleteLogsToday(), {
          params: this.cardNumber ? { cardNumber: this.cardNumber } : {},
          headers: token
            ? {
                Authorization: `Bearer ${safeToken}`
              }
            : {}
        })
        const data = response?.data || {}
        if (Number(data.code) === 200) {
          this.todayDeleteLogs = Array.isArray(data.data) ? data.data : []
        } else {
          this.todayDeleteLogs = []
          this.actionMessage = data.msg || data.message || '获取当天删除记录失败'
        }
      } catch (error) {
        this.todayDeleteLogs = []
        this.actionMessage = error.response?.data?.msg || error.response?.data?.message || '获取当天删除记录失败'
      } finally {
        this.deleteLogsLoading = false
      }
    },
    async exportMaterialPatrolLogs() {
      if (this.exportingPatrolLogs) {
        return
      }
      this.exportingPatrolLogs = true
      this.actionMessage = ''
      try {
        const token = localStorage.getItem('authToken')
        const safeToken = this.toSafeHeaderValue(token)
        const response = await axios.get(API_CONFIG.jobMaterialPatrolLogExport(), {
          params: this.cardNumber ? { cardNumber: this.cardNumber } : {},
          responseType: 'blob',
          headers: token
            ? {
                Authorization: `Bearer ${safeToken}`
              }
            : {}
        })
        const blob = new Blob([response.data], {
          type: response.headers?.['content-type'] || 'application/octet-stream'
        })
        const fileName = this.getDownloadFileName(response, '素材自动处理巡检日志.xlsx')
        this.triggerBlobDownload(blob, fileName)
        this.actionMessage = '巡检日志导出成功'
      } catch (error) {
        this.actionMessage = error.response?.data?.msg || error.response?.data?.message || '巡检日志导出失败'
      } finally {
        this.exportingPatrolLogs = false
      }
    },
    parsePositiveInt(value) {
      const num = Number(value)
      if (!Number.isFinite(num) || num <= 0) {
        return 0
      }
      return Math.floor(num)
    },
    buildSmartBoostPayload(item, enable, customBudget, customDuration) {
      const advertiserId = this.getJobAdvertiserId(item)
      const adId = this.getJobAdId(item)
      if (!advertiserId || !adId) {
        return null
      }
      const payload = {
        advertiser_id: String(advertiserId),
        ad_id: String(adId),
        enable: !!enable
      }
      if (enable) {
        const budget = this.parsePositiveInt(customBudget != null ? customBudget : this.boostBudget)
        const duration = this.parsePositiveInt(customDuration != null ? customDuration : this.boostDuration)
        if (!budget || !duration) {
          return null
        }
        payload.budget = budget
        payload.duration = duration
      }
      return payload
    },
    async requestSmartBoost(payload, requestUsername) {
      const token = localStorage.getItem('authToken')
      const accessToken = localStorage.getItem('accessToken') || token || ''
      const safeToken = this.toSafeHeaderValue(token)
      const username = this.toSafeHeaderValue(requestUsername || '')
      const headers = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers.Authorization = `Bearer ${safeToken}`
      }
      if (username) {
        headers.username = username
      }
      const requestBody = {
        ...payload,
        accessToken: String(accessToken || '')
      }
      const response = await axios.post(API_CONFIG.smartBoostSwitch(), requestBody, { headers })
      return response?.data || {}
    },
    async handleBatchBoost(enable) {
      if (this.batchActionLoading) {
        return
      }
      const targetList = this.jobList || []
      if (targetList.length === 0) {
        this.actionMessage = '暂无可操作数据'
        return
      }
      if (enable) {
        const budget = this.parsePositiveInt(this.boostBudget)
        const duration = this.parsePositiveInt(this.boostDuration)
        if (!budget || !duration) {
          this.actionMessage = '一键起量前请先填写预算和时长（正整数）'
          return
        }
      }
      this.batchActionLoading = true
      this.actionMessage = ''
      let successCount = 0
      let failCount = 0
      for (const item of targetList) {
        const payload = this.buildSmartBoostPayload(item, enable)
        const requestUsername = this.getJobUsername(item)
        if (!payload) {
          failCount += 1
          continue
        }
        if (!requestUsername) {
          failCount += 1
          continue
        }
        try {
          const data = await this.requestSmartBoost(payload, requestUsername)
          if (Number(data.code) === 200) {
            successCount += 1
          } else {
            failCount += 1
          }
        } catch (error) {
          failCount += 1
        }
      }
      this.batchActionLoading = false
      this.actionMessage = `一键${enable ? '起量' : '停止'}完成（全量任务）：成功 ${successCount} 条，失败 ${failCount} 条`
      this.fetchJobList({ silent: true })
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.autoRefreshTimer = setInterval(() => {
        this.fetchJobList({ silent: true })
      }, 20000)
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },
    async fetchJobList(options = {}) {
      const { silent = false } = options
      if (this.listRequesting) {
        return
      }
      this.listRequesting = true
      if (!silent) {
        this.loading = true
        this.errorMessage = ''
      }
      try {
        const token = localStorage.getItem('authToken')
        const safeToken = this.toSafeHeaderValue(token)
        const response = await axios.get(API_CONFIG.jobList(), {
          params: this.cardNumber ? { cardNumber: this.cardNumber } : {},
          headers: token
            ? {
                Authorization: `Bearer ${safeToken}`
              }
            : {}
        })
        if (response.data && response.data.code === 200) {
          const rows = response.data.rows || response.data.data || []
          this.jobList = this.normalizeJobList(rows)
        } else {
          if (!silent) {
            this.errorMessage = (response.data && (response.data.msg || response.data.message)) || '获取数据概览失败'
            this.jobList = []
          }
        }
      } catch (error) {
        if (!silent) {
          this.errorMessage = error.response?.data?.msg || error.response?.data?.message || '获取数据概览失败'
          this.jobList = []
        }
      } finally {
        this.listRequesting = false
        if (!silent) {
          this.loading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.overview-container {
  min-height: 100vh;
  height: 100vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.overview-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 14px 0;
}

.header-content {
  width: min(calc(100% - 32px), 1400px);
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 20px;
}

.delete-log-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1200;
}

.delete-log-dialog {
  width: min(980px, 100%);
  max-height: 85vh;
  overflow: hidden;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
}

.delete-log-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
}

.delete-log-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.delete-log-subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #64748b;
}

.delete-log-close {
  border: none;
  border-radius: 10px;
  padding: 8px 14px;
  background: #e2e8f0;
  color: #0f172a;
  cursor: pointer;
}

.delete-log-status {
  padding: 32px 24px;
  text-align: center;
  color: #64748b;
}

.delete-log-list {
  overflow-y: auto;
  padding: 20px 24px 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.delete-log-card {
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 16px;
  padding: 16px;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
}

.delete-log-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.delete-log-key {
  color: #64748b;
  font-size: 13px;
  flex: 0 0 72px;
}

.delete-log-value {
  color: #0f172a;
  font-size: 13px;
  text-align: right;
  word-break: break-all;
}

.delete-log-reason {
  margin-top: 8px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  font-size: 13px;
  line-height: 1.6;
}

.back-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  padding: 10px 20px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
}

.header-title h1 {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.overview-content {
  flex: 1;
  width: min(calc(100% - 32px), 1400px);
  margin: 0 auto;
  padding: 14px 0 18px;
  overflow: hidden;
  min-height: 0;
  box-sizing: border-box;
}

.overview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-title {
  color: rgba(255, 255, 255, 0.92);
  font-size: 18px;
  font-weight: 600;
}

.toolbar-count {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toolbar-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
}

.toolbar-select {
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.12);
  color: white;
  padding: 6px 8px;
  min-width: 130px;
}

.toolbar-select option {
  color: #1f2a44;
}

.action-button {
  border: none;
  border-radius: 999px;
  color: #fff;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
}

.action-button.start {
  background: linear-gradient(45deg, #2ecc71, #27ae60);
}

.action-button.stop {
  background: linear-gradient(45deg, #ff6b6b, #ee5253);
}

.action-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.refresh-button {
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  padding: 8px 14px;
  cursor: pointer;
}

.summary-entry-button {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.42);
  font-weight: 600;
}

.refresh-button:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.status-message {
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  margin-bottom: 12px;
}

.status-message.error {
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.4);
  color: #ff9f9f;
}

.status-message.empty {
  background: rgba(255, 255, 255, 0.1);
  border: 1px dashed rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.82);
  text-align: center;
}

.status-message.action {
  background: rgba(46, 204, 113, 0.2);
  border: 1px solid rgba(46, 204, 113, 0.45);
  color: #c8f7dc;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.summary-item {
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  padding: 10px 12px;
}

.summary-label {
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
}

.summary-value {
  margin-top: 4px;
  color: #ffffff;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
}

.job-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.job-card {
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  padding: 12px 14px;
}

.job-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.job-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.88);
  color: #3a4d71;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.job-base {
  min-width: 0;
  flex: 1;
}

.target-name {
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.15;
  word-break: break-all;
}

.target-id {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 18px;
  line-height: 1.2;
}

.job-meta {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
}

.auto-material-row {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.auto-material-label {
  color: rgba(255, 255, 255, 0.86);
  font-size: 13px;
}

.auto-material-button {
  border: none;
  border-radius: 8px;
  padding: 6px 10px;
  color: #fff;
  cursor: pointer;
  font-size: 12px;
}

.auto-material-button.enable {
  background: linear-gradient(45deg, #2ecc71, #27ae60);
}

.auto-material-button.disable {
  background: linear-gradient(45deg, #ff6b6b, #ee5253);
}

.auto-material-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.metrics-grid {
  display: grid;
  gap: 10px;
}

.primary-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.secondary-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 6px;
}

.metric-item {
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
  padding: 10px 12px;
}

.metric-item.secondary {
  background: rgba(255, 255, 255, 0.04);
}

.metric-value {
  color: #ffffff;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 800;
}

.secondary-value {
  font-size: 24px;
}

.metric-label {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.76);
  font-size: 16px;
  line-height: 1.2;
}

@media (max-width: 992px) {
  .overview-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-left {
    justify-content: space-between;
  }

  .toolbar-right {
    justify-content: space-between;
  }

  .primary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .secondary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .target-name {
    font-size: 24px;
  }

  .metric-value {
    font-size: 24px;
  }

  .metric-label {
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .overview-container {
    min-height: 100vh;
    height: auto;
    overflow-y: auto;
  }

  .overview-content {
    padding: 10px 8px 16px;
    overflow: visible;
  }

  .header-content {
    padding: 0 10px;
    gap: 10px;
    flex-wrap: wrap;
  }

  .back-button {
    padding: 7px 12px;
    font-size: 12px;
    border-radius: 18px;
  }

  .header-title h1 {
    font-size: 18px;
  }

  .overview-toolbar {
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.1);
    padding: 8px;
    margin-bottom: 8px;
  }

  .toolbar-left,
  .toolbar-right {
    width: 100%;
  }

  .toolbar-right {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .toolbar-label {
    justify-content: space-between;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
    padding: 7px 8px;
    font-size: 12px;
  }

  .toolbar-select {
    min-width: 132px;
    font-size: 12px;
    padding: 5px 7px;
  }

  .refresh-button {
    width: 100%;
    border-radius: 8px;
    text-align: center;
    padding: 7px 10px;
    font-size: 12px;
  }

  .action-button {
    width: 100%;
    border-radius: 8px;
    padding: 7px 10px;
    font-size: 12px;
  }

  .primary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    margin-bottom: 8px;
  }

  .secondary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }

  .summary-item {
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.14);
    padding: 8px;
  }

  .summary-value {
    font-size: 18px;
  }

  .job-card-list {
    gap: 8px;
  }

  .job-card {
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.26);
    background: rgba(255, 255, 255, 0.12);
    padding: 10px;
  }

  .job-card-header {
    margin-bottom: 8px;
  }

  .target-name {
    font-size: 18px;
  }

  .target-id {
    font-size: 12px;
  }

  .job-meta {
    font-size: 12px;
    line-height: 1.45;
  }

  .auto-material-row {
    gap: 6px;
  }

  .metric-item {
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.11);
    padding: 8px;
  }

  .metric-value {
    font-size: 18px;
  }

  .secondary-value {
    font-size: 16px;
  }

  .metric-label {
    font-size: 12px;
  }
}

@media (max-width: 576px) {
  .overview-header {
    padding: 8px 0;
  }

  .header-content {
    align-items: flex-start;
  }

  .back-button {
    padding: 6px 10px;
    font-size: 11px;
  }

  .header-title {
    width: 100%;
    gap: 8px;
  }

  .title-icon {
    font-size: 18px;
  }

  .header-title h1 {
    font-size: 16px;
  }

  .toolbar-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .toolbar-label {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }

  .toolbar-select {
    width: 100%;
    min-width: 0;
  }

  .summary-grid,
  .primary-grid,
  .secondary-grid {
    grid-template-columns: 1fr;
  }

  .summary-value {
    font-size: 16px;
  }

  .job-card {
    padding: 8px;
  }

  .job-card-header {
    align-items: flex-start;
  }

  .job-avatar {
    width: 34px;
    height: 34px;
    font-size: 15px;
  }

  .target-name {
    font-size: 16px;
  }

  .target-id {
    font-size: 11px;
  }

  .job-meta {
    line-height: 1.5;
  }

  .auto-material-button {
    width: 100%;
  }


  .metric-value {
    font-size: 16px;
  }

  .secondary-value {
    font-size: 15px;
  }

  .metric-label {
    font-size: 11px;
  }
}
</style>
